import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { useInfiniteHandler, useQueryToast } from "hooks";
import i18next from "i18next";
import { IInfinitePageParams, IInfinitePageResponse } from "services/types";
import { apiClient } from "utils";
import { getDefaultInfinitePageParam } from "utils/service/defaultInfinitePageParam";
import { useResponseHandler } from "utils/service/responseHandler";
import {
  IAddVideoById,
  IChannelPageResponse,
  IChannelResponse,
  IChannelSearch,
  IChannelVideoRemove,
  IChannelVideoResponse,
  ICreateChannelById,
  ICreateChannelByLink,
  ICreateChannelContract,
  IUpdateChannel,
  IUpdateVideoContract,
} from "./types/query";

export const useGetChannelListInfinite = (params: IChannelSearch) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IChannelResponse[], DefaultError>({
    queryKey: ["Channel-list", params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: "Channel",
        params: { ...params, itemCount, page: pageParam },
      });

      const value = await responseHandler<IChannelPageResponse>(response, {
        groupName: `useGetChannelListInfinite - ${pageParam}`,
      });

      return infiniteHandler<IChannelResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetChannel = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IChannelResponse, DefaultError>({
    queryKey: ["channel", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Channel/${id}`,
      });

      return responseHandler(response, {
        groupName: `channel - ${id}`,
        notFoundPath: "/channel",
      });
    },
  });
};

export const usePostChannelById = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePostChannel");
  return useMutation<IChannelResponse, DefaultError, ICreateChannelById>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.create.loading", {
          what: i18next.t("channel.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "Channel",
        body,
      });

      return responseHandler(response, {
        groupName: "usePostChannelById",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.create.success", {
          what: data.name ?? i18next.t("channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostChannelByLink = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePostChannel");
  return useMutation<IChannelResponse, DefaultError, ICreateChannelByLink>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.create.loading", {
          what: i18next.t("channel.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "Channel/Link",
        body,
      });

      return responseHandler(response, {
        groupName: "usePostChannelByLink",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.create.success", {
          what: data.name ?? i18next.t("channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateChannel = (channelId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useUpdateChannel");
  return useMutation<IChannelResponse, DefaultError, IUpdateChannel>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("Channel.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "Channel",
        body: { channelId },
      });

      return responseHandler(response, {
        groupName: "useUpdateChannel",
        notFoundPath: "/channel",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: data.name ?? i18next.t("Channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};
export const useSyncAllChannels = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useSyncAllChannels");
  return useMutation<IChannelResponse, DefaultError, {}>({
    mutationFn: async (_) => {
      const response = await apiClient({
        method: "PUT",
        url: "Channel/Sync/All",
        body: {},
      });

      return responseHandler(response, {
        groupName: "useSyncAllChannels",
        notFoundPath: "/channel",
      });
    },
    onSuccess: (data) => {},
    onError: dismiss,
  });
};
export const useSyncAllChannelVideos = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useSyncAllChannelVideos"
  );
  return useMutation<IChannelResponse, DefaultError, {}>({
    mutationFn: async (_) => {
      const response = await apiClient({
        method: "PUT",
        url: "Channel/Videos/Sync/All",
        body: {},
      });

      return responseHandler(response, {
        groupName: "useSyncAllChannelVideos",
        notFoundPath: "/channel",
      });
    },
    onSuccess: (data) => {},
    onError: dismiss,
  });
};

export const useDeleteChannel = (id: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useDeleteChannel");
  return useMutation<{}, DefaultError>({
    async mutationFn() {
      loading(
        i18next.t("service-message.delete.loading", {
          what: i18next.t("channel.what"),
        })
      );
      const response = await apiClient({
        method: "DELETE",
        url: `Channel/${id}`,
      });

      return responseHandler(response, {
        groupName: "useDeleteChannel",
        notFoundPath: "/channel",
      });
    },

    onSuccess: (data) => {
      success(
        i18next.t("service-message.delete.success", {
          what: i18next.t("channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostChannelVideo = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("usePostChannel");
  return useMutation<IChannelVideoResponse, DefaultError, ICreateChannelById>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.add.loading", {
          what: i18next.t("channel.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "Channel/videos",
        body,
      });

      return responseHandler(response, {
        groupName: "usePostChannelVideo",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.add.success", {
          what: data.id ?? i18next.t("channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateChannelVideo = (channelId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useUpdateChannelVideo");
  return useMutation<IChannelVideoResponse, DefaultError, IUpdateChannel>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.update.loading", {
          what: i18next.t("Channel.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: `Channel/videos/${channelId}`,
      });

      return responseHandler(response, {
        groupName: "useUpdateChannelVideo",
        notFoundPath: "/channel",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.update.success", {
          what: data.id ?? i18next.t("Channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useGetChannelVideosInfinite = (
  channelId: string,
  params?: IInfinitePageParams,
  enabled?: boolean
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params?.page,
    params?.itemCount
  );

  return useInfiniteQuery<IChannelVideoResponse[], DefaultError>({
    queryKey: ["channel-videos", channelId, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: "GET",
        url: "Channel/Videos/All",
        params: { itemCount, page: pageParam, channelId },
      });

      const value = await responseHandler<
        IInfinitePageResponse<IChannelVideoResponse>
      >(response, {
        groupName: `useGetChannelVideosInfinite - ${pageParam}`,
      });

      return infiniteHandler<IChannelVideoResponse>(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
    enabled: enabled,
  });
};

// export const useGetChannelVideos = (id: string) => {
//   const { responseHandler } = useResponseHandler();
//   return useQuery<IChannelVideoResponse[], DefaultError>({
//     queryKey: ["video", id],
//     async queryFn() {
//       const response = await apiClient({
//         method: "GET",
//         url: `Channel/videos`,
//         params: {ChannelId: id}
//       });

//       return responseHandler(response, {
//         groupName: `videos for chanell - ${id}`,
//         notFoundPath: "/channel",
//       });
//     },
//   });
// };
export const useGetChannelVideo = (id: string) => {
  const { responseHandler } = useResponseHandler();
  return useQuery<IChannelVideoResponse, DefaultError>({
    queryKey: ["video", id],
    async queryFn() {
      const response = await apiClient({
        method: "GET",
        url: `Channel/video`,
        params: { VideoId: id },
      });

      return responseHandler(response, {
        groupName: `videos for chanell - ${id}`,
        notFoundPath: "/channel",
      });
    },
  });
};

export const useDeleteChannelVideo = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast("useDeleteChannelVideo");
  return useMutation<{}, DefaultError, IChannelVideoRemove>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.delete.loading", {
          what: i18next.t("channel.video.what"),
        })
      );
      const response = await apiClient({
        method: "DELETE",
        url: `Channel/videos`,
        body,
      });

      return responseHandler(response, {
        groupName: "useDeleteChannelVideo",
        notFoundPath: "/channel",
      });
    },

    onSuccess: (data) => {
      success(
        i18next.t("service-message.delete.success", {
          what: i18next.t("channel.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const useUpdateChannelContracts = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useUpdateChannelContracts"
  );
  return useMutation<{}, DefaultError, ICreateChannelContract>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.add.loading", {
          what: i18next.t("contract-template.contract.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "Channel",
        body,
      });

      return responseHandler(response, {
        groupName: "useUpdateChannelContracts",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.add.success", {
          what: i18next.t("contract-template.contract.what"),
        })
      );
    },
    onError: dismiss,
  });
};
export const useUpdateChannelVideoContracts = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "useUpdateChannelVideoContracts"
  );
  return useMutation<{}, DefaultError, IUpdateVideoContract>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.add.loading", {
          what: i18next.t("contract-template.contract.what"),
        })
      );
      const response = await apiClient({
        method: "PUT",
        url: "Channel/Videos/Contract",
        body,
      });

      return responseHandler(response, {
        groupName: "useUpdateChannelVideoContracts",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.add.success", {
          what: i18next.t("contract-template.contract.what"),
        })
      );
    },
    onError: dismiss,
  });
};

export const usePostChannelVideoById = (channelId: string) => {
  const { responseHandler } = useResponseHandler();
  const { loading, dismiss, success } = useQueryToast(
    "usePostChannelVideoById"
  );
  return useMutation<IChannelResponse, DefaultError, IAddVideoById>({
    mutationFn: async (body) => {
      loading(
        i18next.t("service-message.create.loading", {
          what: i18next.t("channel.what"),
        })
      );
      const response = await apiClient({
        method: "POST",
        url: "Channel/AddVideoToChannel",
        body: { ...body, channelId },
      });

      return responseHandler(response, {
        groupName: "usePostChannelVideoById",
      });
    },
    onSuccess: (data) => {
      success(
        i18next.t("service-message.create.success", {
          what: data.name ?? i18next.t("video.what"),
        })
      );
    },
    onError: dismiss,
  });
};
